import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  listProjects,
  getProject,
  Project,
  ProjectSummary,
} from '../services/api';
import { useAuth } from './AuthContext';

interface ProjectContextType {
  projects: ProjectSummary[];
  activeProjectId: string | null;
  activeProject: Project | null;
  loading: boolean;
  refreshProjects: () => Promise<void>;
  refreshActiveProject: () => Promise<void>;
  selectProject: (projectId: string | null) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, getAccessToken } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Ask AuthContext for a token — it will install it on axios when available.
  // Do NOT clear the header on null; other code paths may already have set it.
  const ensureAuth = useCallback(async () => {
    try { await getAccessToken(); } catch { /* ignore; request will 401 and be caught */ }
  }, [getAccessToken]);

  const refreshProjects = useCallback(async () => {
    try {
      await ensureAuth();
      const list = await listProjects();
      setProjects(list);
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  }, [ensureAuth]);

  const refreshActiveProject = useCallback(async () => {
    if (!activeProjectId) {
      setActiveProject(null);
      return;
    }
    try {
      setLoading(true);
      await ensureAuth();
      const project = await getProject(activeProjectId);
      setActiveProject(project);
    } catch (err) {
      console.error('Failed to load active project', err);
      setActiveProject(null);
    } finally {
      setLoading(false);
    }
  }, [activeProjectId, ensureAuth]);

  const selectProject = useCallback(async (projectId: string | null) => {
    setActiveProjectId(projectId);
    if (!projectId) {
      setActiveProject(null);
      return;
    }
    try {
      setLoading(true);
      await ensureAuth();
      const project = await getProject(projectId);
      setActiveProject(project);
    } catch (err) {
      console.error('Failed to select project', err);
      setActiveProject(null);
    } finally {
      setLoading(false);
    }
  }, [ensureAuth]);

  // Wait until AuthContext has a real user (which means getAccessToken will
  // resolve to a real token) before firing the initial list. Gate on the stable
  // id so we don't re-fire whenever AuthContext rebuilds its user object.
  const userId = user?.localAccountId;
  useEffect(() => {
    if (!userId) return;
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const value = useMemo<ProjectContextType>(() => ({
    projects,
    activeProjectId,
    activeProject,
    loading,
    refreshProjects,
    refreshActiveProject,
    selectProject,
  }), [projects, activeProjectId, activeProject, loading, refreshProjects, refreshActiveProject, selectProject]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjects = (): ProjectContextType => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjects must be used within a ProjectProvider');
  return ctx;
};
